import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatNumberLocale } from "@/utils/helpers";
import { useIntl } from "react-intl";
import { Dispatch, SetStateAction } from "react";
import { TransactionsModals, SharedModals } from "@/constants/enums";
import { useTranslation } from "react-i18next";
import { FramerButton } from "@/components/motion";

type Props = {
  amount: number;
  showModal: TransactionsModals | SharedModals;
  setShowModal: Dispatch<SetStateAction<TransactionsModals>>;
  handleTransactionMutation: () => void;
}

function WithdrawalTransactionSummaryModal(props: Props) {
  // Props
  const {
    amount,
    showModal,
    setShowModal,
    handleTransactionMutation,
  } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["modals"]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(TransactionsModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === TransactionsModals.WithdrawalSummary} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl dark:text-white text-center font-bold">
            {t("investmenttransactionsummary.title")}
          </DialogTitle>
        </DialogHeader>

        {/* Subtext */}
        <div className="mb-4">
          <p className="dark:text-white text-lg">
            {t("withdrawalsummary.subtext")}
            <span className="pl-1 font-semibold text-mforange">
              {formatNumberLocale(intl, amount, "currency")}
            </span>
          </p>
        </div>

        {/* Disclaimer */}
        <div className="rounded bg-mfpurple p-4 mt-4 mb-8 text-sm">
          <p className="text-white font-semibold">
            {t("investmenttransactionsummary.note.title")}:
          </p>
          <p className="text-white">
            {t("withdrawalsummary.disclaimer")}
          </p>
        </div>

        <DialogFooter>
          {/* Buttons */}
          <div className="flex items-center justify-between w-full">
            <FramerButton
              variant="outline-primary"
              onClick={() => setShowModal(TransactionsModals.AddTransaction)}
            >
              {t("common:buttons.back")}
            </FramerButton>
            <FramerButton
              onClick={() => handleTransactionMutation()}
              className="bg-mforange text-white hover:bg-mforange/80"
            >
              {t("common:buttons.submit")}
            </FramerButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WithdrawalTransactionSummaryModal;
